import { Flame, Trophy, Calendar } from 'lucide-react';
import { useStudyStreak } from '@/hooks/useStudyStreak';
import { cn } from '@/lib/utils';

interface StudyStreakProps {
  compact?: boolean;
  className?: string;
}

const StudyStreak = ({ compact = false, className }: StudyStreakProps) => {
  const { streak, loading } = useStudyStreak();

  if (loading) {
    return (
      <div className={cn("animate-pulse bg-muted rounded-lg h-12", className)} />
    );
  }

  if (!streak) return null;

  const hasActiveStreak = streak.current_streak > 0;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/50", className)}>
        <Flame 
          className={cn(
            "w-4 h-4 transition-all",
            hasActiveStreak ? "text-destructive animate-pulse" : "text-muted-foreground"
          )} 
        />
        <span className={cn(
          "text-sm font-semibold",
          hasActiveStreak ? "text-destructive" : "text-muted-foreground"
        )}>
          {streak.current_streak}
        </span>
      </div>
    );
  }

  return (
    <div className={cn(
      "bg-gradient-to-br from-destructive/10 to-destructive/5 border border-destructive/20 rounded-xl p-4",
      className
    )}>
      <div className="flex items-center gap-3 mb-4">
        <div className={cn(
          "w-12 h-12 rounded-full flex items-center justify-center",
          hasActiveStreak 
            ? "bg-gradient-to-br from-destructive to-destructive/80" 
            : "bg-muted"
        )}>
          <Flame 
            className={cn(
              "w-6 h-6",
              hasActiveStreak ? "text-white animate-pulse" : "text-muted-foreground"
            )} 
          />
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">
            {streak.current_streak} day{streak.current_streak !== 1 ? 's' : ''}
          </p>
          <p className="text-sm text-muted-foreground">Current streak</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 bg-background/50 rounded-lg p-2">
          <Trophy className="w-4 h-4 text-accent-foreground" />
          <div>
            <p className="text-sm font-semibold">{streak.longest_streak}</p>
            <p className="text-xs text-muted-foreground">Best streak</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-background/50 rounded-lg p-2">
          <Calendar className="w-4 h-4 text-primary" />
          <div>
            <p className="text-sm font-semibold">{streak.total_study_days}</p>
            <p className="text-xs text-muted-foreground">Total days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyStreak;
